import { CanopyProvider, ControlPanel } from '@bloomfieldrobotics/canopy';
import { Divider, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { useUrlReduxBidirectionalSync } from '../../hooks/useUrlReduxBidirectionalSync';
import { Ranch } from '../../services/entities/impl/blfApi/schemas/ranch';
import { CustomerControlSection } from '../commonUI/ControlSections/CustomerControlSection';
import { RanchToggleControlSection } from '../commonUI/ControlSections/RanchToggleControlSection';
import {
  selectCurrCustomerID,
  setCurrCustomerByID,
} from '../select/selectSlice';
import { CollectionsExplorer } from './components/CollectionsExplorer';
import { useCollectionsPageParams } from './hooks/useCollectionsPageParams';

export const CollectionsPage = () => {
  const { params, updateParams } = useCollectionsPageParams();
  const { customerID } = params;

  // Bidirectional sync: URL ⟷ Redux for customerID
  useUrlReduxBidirectionalSync({
    urlValue: customerID,
    updateUrl: (newCustomerID: string) =>
      updateParams({ customerID: newCustomerID }),
    reduxSelector: selectCurrCustomerID,
    reduxAction: setCurrCustomerByID,
  });

  const [isLoadingRanches, setIsLoadingRanches] = useState(true);
  const [selectedRanchIds, setSelectedRanchIds] = useState<string[]>([]);

  const handleRanchToggle = useCallback((activeRanchIds: string[]) => {
    setSelectedRanchIds(activeRanchIds);
  }, []);

  const handleRanchesLoad = useCallback((ranches: Ranch[] | undefined) => {
    // Auto-select all ranches when they load
    if (ranches?.length) {
      setSelectedRanchIds(ranches.map((ranch) => ranch.id));
    }
    setIsLoadingRanches(false);
  }, []);

  const handleCustomerChange = useCallback(
    (newCustomerId: string) => {
      updateParams({ customerID: newCustomerId });
      setSelectedRanchIds([]); // Reset ranch selection when customer changes
      setIsLoadingRanches(true);
    },
    [updateParams]
  );

  return (
    <CanopyProvider>
      <Stack direction='row' sx={{ width: '100%', height: '100%' }}>
        <ControlPanel>
          <CustomerControlSection
            value={customerID}
            onChange={handleCustomerChange}
            editable
          />
          <Divider flexItem />
          <RanchToggleControlSection
            customerId={customerID}
            onRanchToggle={handleRanchToggle}
            onRanchesLoad={handleRanchesLoad}
            key={customerID}
          />
          <Divider flexItem />
        </ControlPanel>
        <CollectionsExplorer
          ranchIds={selectedRanchIds}
          loading={isLoadingRanches}
        />
      </Stack>
    </CanopyProvider>
  );
};
